import { type ReactNode, useState, useCallback, useRef } from "react";
import { useInput, InputHelperText } from "react-admin";
import { TextField, InputAdornment, FormHelperText } from "@mui/material";
import { validatePercentageRange } from "@/utils/validators/inPlaceValidators";

type DisplayFormat = "dollar" | "percent" | "number";

interface FormattedNumberInputProps {
  /** react-admin field source path */
  source: string;
  /** Display label */
  label?: string | false;
  /** Format type determines adornment and formatting rules */
  displayFormat?: DisplayFormat;
  /** react-admin variant prop */
  variant?: "filled" | "outlined" | "standard";
  /** Full width */
  fullWidth?: boolean;
  /** Helper text shown below the input */
  helperText?: ReactNode;
  /** onChange callback (for manual-edit detection) */
  onChange?: () => void;
  /** Disable the input */
  disabled?: boolean;
  /** Size */
  size?: "small" | "medium";
}

/**
 * Percentage fields store a fraction (e.g. 0.9) but are entered and displayed
 * as a whole percent (90). Scale ×100 on display, ÷100 on parse.
 */
const PERCENT_FACTOR = 100;

/** Strip float noise introduced by the ×100 / ÷100 percent conversions. */
const trimFloatNoise = (n: number): number => Math.round(n * 1e6) / 1e6;

/** Convert a stored raw value to the number shown to the user. */
const toDisplayNumber = (value: number, fmt: DisplayFormat): number =>
  fmt === "percent" ? trimFloatNoise(value * PERCENT_FACTOR) : value;

/** Format a raw number for display based on format type */
const formatDisplay = (value: number | null | undefined, fmt: DisplayFormat): string => {
  if (value === null || value === undefined) return "";

  const shown = toDisplayNumber(value, fmt);
  switch (fmt) {
    case "dollar":
      return shown.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case "percent":
    case "number":
      return shown.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
  }
};

/** The unformatted string shown while the field is focused (for easy editing). */
const toEditString = (value: number | null | undefined, fmt: DisplayFormat): string =>
  value === null || value === undefined ? "" : String(toDisplayNumber(value, fmt));

/** Parse a display string back to a raw (stored) number */
const parseValue = (text: string, fmt: DisplayFormat): number | null => {
  const cleaned = text.replace(/[,$%\s]/g, "");
  if (cleaned === "") return null;
  const num = parseFloat(cleaned);
  if (isNaN(num)) return null;
  return fmt === "percent" ? trimFloatNoise(num / PERCENT_FACTOR) : num;
};

/**
 * A number input that shows formatted values (commas, $, %) on blur
 * and raw numbers on focus for easy editing. Connects to react-admin
 * form state via useInput.
 */
export const FormattedNumberInput = ({
  source,
  label,
  displayFormat = "number",
  variant = "filled",
  fullWidth = true,
  helperText,
  onChange: onChangeProp,
  disabled = false,
  size = "small",
}: FormattedNumberInputProps) => {
  const {
    field,
    fieldState: { error, isTouched },
    isRequired,
  } = useInput({
    source,
    // Percent fields store a fraction; cap input at 0–100% so a typo (e.g. 150)
    // is caught before save, matching InPlacePercentageEditor's display-side guard.
    validate: displayFormat === "percent" ? validatePercentageRange : undefined,
  });

  const [focused, setFocused] = useState(false);
  const [localText, setLocalText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const rawValue = field.value as number | null | undefined;

  // Display value: raw text while focused (preserves decimal point & trailing zeros), formatted on blur
  const displayValue = focused ? localText : formatDisplay(rawValue, displayFormat);

  const handleFocus = useCallback(() => {
    setFocused(true);
    setLocalText(toEditString(rawValue, displayFormat));
  }, [rawValue, displayFormat]);

  const handleBlur = useCallback(() => {
    const parsed = parseValue(localText, displayFormat);
    field.onChange(parsed);
    setFocused(false);
    field.onBlur();
  }, [field, localText, displayFormat]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalText(e.target.value);
      const parsed = parseValue(e.target.value, displayFormat);
      field.onChange(parsed);
      onChangeProp?.();
    },
    [field, onChangeProp, displayFormat]
  );

  const startAdornment = displayFormat === "dollar" ? <InputAdornment position="start">$</InputAdornment> : undefined;

  const endAdornment = displayFormat === "percent" ? <InputAdornment position="end">%</InputAdornment> : undefined;

  const showError = isTouched && !!error;

  return (
    <>
      <TextField
        inputRef={inputRef}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        label={label}
        variant={variant}
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        required={isRequired}
        error={showError}
        slotProps={{
          input: {
            startAdornment,
            endAdornment,
          },
          htmlInput: {
            inputMode: "decimal",
          },
        }}
      />
      {(showError || helperText) && (
        <FormHelperText error={showError} sx={{ mx: 1.75 }}>
          {showError ? <InputHelperText error={error?.message} /> : helperText}
        </FormHelperText>
      )}
    </>
  );
};
